import axiosClient from "utils/axios";
import {
  booleanQueryParam,
  commaSeparatedEnums,
  parseOptionalTrimmedString,
  positiveInt,
} from "utils/list-query";
import { ApiResponse } from "../../../types/api.types";
import {
  EnquirySiteVisitPriority,
  EnquirySiteVisitStatus,
} from "features/EnquirySiteVisits/constants/enquiry-site-visit.constants";
import {
  AddEnquirySiteVisitRequest,
  EnquirySiteVisit,
  EnquirySiteVisitAssignmentSummary,
  GetEnquirySiteVisitsResponse,
  ScheduleEnquirySiteVisitRequest,
  SiteVisitChecklistItem,
  UpdateEnquirySiteVisitRequest,
} from "features/EnquirySiteVisits/types/enquiry-site-visit.types";
import { normalizeWorkAssignment } from "features/WorkAssignments/utils/workAssignmentNormalize";
import { Employee } from "features/Employees/types/employee.types";

const SITE_VISIT_LIST_STATUSES = Object.values(EnquirySiteVisitStatus);
const SITE_VISIT_LIST_PRIORITIES = Object.values(EnquirySiteVisitPriority);

function trimQueryDate(v: string | undefined): string | undefined {
  const t = v?.trim();
  return t ? t : undefined;
}

function normalizeSiteVisitListRow(row: EnquirySiteVisit): EnquirySiteVisit {
  const r = row as unknown as Record<string, unknown>;
  const fromAssignments = Array.isArray(r.assignments) ? (r.assignments as unknown[]) : [];
  const fromWorkAssignments = Array.isArray(r.workAssignments) ? (r.workAssignments as unknown[]) : [];
  const fromSnake = Array.isArray(r.work_assignments) ? (r.work_assignments as unknown[]) : [];
  const seen = new Set<number>();
  const arr: unknown[] = [];
  for (const item of [...fromAssignments, ...fromWorkAssignments, ...fromSnake]) {
    const id = Number((item as Record<string, unknown>).id);
    if (id > 0 && seen.has(id)) continue;
    if (id > 0) seen.add(id);
    arr.push(item);
  }
  if (!arr.length) return row;

  const assignments: EnquirySiteVisitAssignmentSummary[] = arr.map((item) => {
    const a = normalizeWorkAssignment(item);
    const u = a.user;
    return {
      id: a.id,
      toUserId: a.toUserId,
      status: a.status ?? null,
      isActive: a.isActive,
      actualEndDate: a.actualEndDate ?? null,
      deleted_at: a.deleted_at ?? null,
      user: u
        ? {
            id: u.id,
            firstName: u.firstName,
            lastName: u.lastName,
            email: u.email,
            avatar: u.avatar,
            employee: u.employee as Employee,
          }
        : null,
    };
  });

  return { ...row, assignments };
}

export type GetEnquirySiteVisitsListParams = {
  page?: number;
  limit?: number;
  search?: string;
  status?: string | string[];
  priority?: string | string[];
  enquiryId?: number | string;
  createdBy?: number | string;
  updatedBy?: number | string;
  clientId?: number | string;
  city?: string;
  state?: string;
  description?: string;
  completedLate?: boolean;
  scheduledAtFrom?: string;
  scheduledAtTo?: string;
  deadlineFrom?: string;
  deadlineTo?: string;
  actualStartFrom?: string;
  actualStartTo?: string;
  completedFrom?: string;
  completedTo?: string;
  createdFrom?: string;
  createdTo?: string;
};

export const getEnquirySiteVisits = async (
  params: GetEnquirySiteVisitsListParams = {},
) => {
  try {
    const {
      page,
      limit,
      search,
      status,
      priority,
      enquiryId,
      createdBy,
      updatedBy,
      clientId,
      city,
      state,
      description,
      completedLate,
      scheduledAtFrom,
      scheduledAtTo,
      deadlineFrom,
      deadlineTo,
      actualStartFrom,
      actualStartTo,
      completedFrom,
      completedTo,
      createdFrom,
      createdTo,
    } = params;

    const query: Record<string, string | number> = {};

    const p = positiveInt(page);
    if (p != null) query.page = p;
    const l = positiveInt(limit);
    if (l != null) query.limit = l;

    const s = search != null ? String(search).trim() : "";
    if (s) query.search = s;

    const statusCsv = commaSeparatedEnums(status, SITE_VISIT_LIST_STATUSES);
    if (statusCsv) query.status = statusCsv;

    const priorityCsv = commaSeparatedEnums(
      priority,
      SITE_VISIT_LIST_PRIORITIES,
    );
    if (priorityCsv) query.priority = priorityCsv;

    const eid = positiveInt(enquiryId);
    if (eid != null) query.enquiryId = eid;

    const cb = positiveInt(createdBy);
    if (cb != null) query.createdBy = cb;

    const ub = positiveInt(updatedBy);
    if (ub != null) query.updatedBy = ub;

    const cid = positiveInt(clientId);
    if (cid != null) query.clientId = cid;

    const cityQ = parseOptionalTrimmedString(city);
    if (cityQ) query.city = cityQ;

    const stateQ = parseOptionalTrimmedString(state);
    if (stateQ) query.state = stateQ;

    const desc = parseOptionalTrimmedString(description);
    if (desc) query.description = desc;

    const late = booleanQueryParam(completedLate);
    if (late !== undefined) query.completedLate = late;

    const dates: Record<string, string | undefined> = {
      scheduledAtFrom: trimQueryDate(scheduledAtFrom),
      scheduledAtTo: trimQueryDate(scheduledAtTo),
      deadlineFrom: trimQueryDate(deadlineFrom),
      deadlineTo: trimQueryDate(deadlineTo),
      actualStartFrom: trimQueryDate(actualStartFrom),
      actualStartTo: trimQueryDate(actualStartTo),
      completedFrom: trimQueryDate(completedFrom),
      completedTo: trimQueryDate(completedTo),
      createdFrom: trimQueryDate(createdFrom),
      createdTo: trimQueryDate(createdTo),
    };
    for (const [k, v] of Object.entries(dates)) {
      if (v) query[k] = v;
    }

    const response = await axiosClient.get<
      ApiResponse<GetEnquirySiteVisitsResponse>
    >("/site-visits", { params: query });
    const body = response.data;
    console.log(body);
    if (body.success && body.data && Array.isArray(body.data.rows)) {
      return {
        ...body,
        data: {
          ...body.data,
          rows: body.data.rows.map((row) => normalizeSiteVisitListRow(row)),
        },
      };
    }
    return body;
  } catch (err) {
    console.warn("Error fetching enquiry site visits : ", err);
    throw err;
  }
};

export const createEnquirySiteVisit = async (payload: FormData) => {
  try {
    const response = await axiosClient.post<
      ApiResponse<AddEnquirySiteVisitRequest>
    >("/site-visits", payload, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  } catch (err) {
    console.warn("Error creating enquiry site visit : ", err);
    throw err;
  }
};

export const updateEnquirySiteVisit = async (id: number, payload: FormData) => {
  try {
    const response = await axiosClient.put<
      ApiResponse<UpdateEnquirySiteVisitRequest>
    >(`/site-visits/${id}`, payload, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  } catch (err) {
    console.warn("Error updating enquiry site visit : ", err);
    throw err;
  }
};

export const scheduleEnquirySiteVisit = async (
  id: number,
  payload: ScheduleEnquirySiteVisitRequest,
) => {
  try {
    const response = await axiosClient.post<ApiResponse<EnquirySiteVisit>>(
      `/site-visits/${id}/schedule`,
      payload,
    );
    return response.data;
  } catch (err) {
    console.warn("Error scheduling enquiry site visit : ", err);
    throw err;
  }
};

export const getEnquirySiteVisit = async (id: number) => {
  try {
    if (!Number.isInteger(id) || id <= 0) {
      throw new Error("Invalid ID");
    }
    const response = await axiosClient.get<ApiResponse<EnquirySiteVisit>>(
      `/site-visits/${id}`,
    );
    const body = response.data;
    if (body.success && body.data) {
      return { ...body, data: normalizeSiteVisitListRow(body.data) };
    }
    return body;
  } catch (err) {
    console.warn("Error fetching enquiry site visit : ", err);
    throw err;
  }
};

export const deleteEnquirySiteVisit = async (id: number) => {
  try {
    const response = await axiosClient.delete<ApiResponse<EnquirySiteVisit>>(
      `/site-visits/${id}`,
    );
    return response.data;
  } catch (err) {
    console.warn("Error deleting enquiry site visit : ", err);
    throw err;
  }
};

export const startEnquirySiteVisit = async (id: number) => {
  const response = await axiosClient.post<ApiResponse<EnquirySiteVisit>>(
    `/site-visits/${id}/start`,
    {},
  );
  return response.data;
};

export const completeEnquirySiteVisit = async (
  id: number,
  body: { description?: string } = {},
) => {
  const response = await axiosClient.post<ApiResponse<EnquirySiteVisit>>(
    `/site-visits/${id}/complete`,
    body,
  );
  return response.data;
};

export const cancelEnquirySiteVisit = async (
  id: number,
  body: { cancellationReason?: string | null } = {},
) => {
  const response = await axiosClient.post<ApiResponse<EnquirySiteVisit>>(
    `/site-visits/${id}/cancel`,
    body,
  );
  return response.data;
};

export const updateSiteVisitChecklist = async (
  id: number,
  checklist: SiteVisitChecklistItem[],
) => {
  const response = await axiosClient.put<ApiResponse<EnquirySiteVisit>>(
    `/site-visits/${id}`,
    { checklist },
  );
  return response.data;
};
